export const formatNumberWithCommas = (
  number: number | null | undefined
): string => {
  if (number !== null && number !== undefined) {
    return number.toLocaleString(undefined, {
      maximumFractionDigits: 4
    });
  }
  return '';
};

const PHILIPPINE_TIME_ZONE = 'Asia/Manila';

const hasExplicitTimeZone = (value: string) =>
  /Z$|[+-]\d{2}:?\d{2}$/.test(value);

const parseStoredUtcDate = (dateTime: string): Date | null => {
  if (!dateTime) return null;
  const normalized = hasExplicitTimeZone(dateTime)
    ? dateTime
    : `${dateTime}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getFormatPart = (
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string => parts.find(part => part.type === type)?.value ?? '';

export const formatDate = (dateTime: string) => {
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const month = monthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  return `${month} ${day} ${year}`;
};

export const formatDateTime12hUtc8 = (dateTime: string) => {
  const date = parseStoredUtcDate(dateTime);
  if (!date) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PHILIPPINE_TIME_ZONE,
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).formatToParts(date);

  const month = getFormatPart(parts, 'month');
  const day = String(Number(getFormatPart(parts, 'day'))).padStart(2, '0');
  const year = getFormatPart(parts, 'year');
  const hour = getFormatPart(parts, 'hour');
  const minute = getFormatPart(parts, 'minute');
  const dayPeriod = getFormatPart(parts, 'dayPeriod');

  return `${month} ${day} ${year} ${hour}:${minute} ${dayPeriod}`;
};

export const formatDateShort = (dateTime: string) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  const shortMonthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const month = shortMonthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

export const formatDateFilter = (date: Date) => {
  return date.toLocaleDateString('en-CA');
};

export type TDisplayCurrency = 'PHP' | 'USD';

export const formatProcurementMoney = (
  phpAmount: number | null | undefined,
  usdAmount: number | null | undefined,
  currency: TDisplayCurrency,
  usdToPhpRate?: number | null
): string => {
  if (currency === 'USD') {
    const usd =
      usdAmount ??
      (phpAmount != null && usdToPhpRate != null && usdToPhpRate > 0
        ? phpAmount / usdToPhpRate
        : undefined);
    if (usd === undefined || usd === null) {
      return '—';
    }
    return `$${usd.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })}`;
  }

  if (phpAmount === null || phpAmount === undefined) {
    return '';
  }

  return phpAmount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  });
};

